import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight, ClipboardCheck, Truck, Home as HomeIcon } from "lucide-react";
import CheckoutSteps from "../components/CheckoutSteps";

function formatINR(n) {
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

const TRACKING_STEPS = [
  { id: "placed", label: "Order Placed", icon: ClipboardCheck, done: true },
  { id: "packed", label: "Packed", icon: Package, done: false },
  { id: "shipped", label: "Shipped", icon: Truck, done: false },
  { id: "delivered", label: "Delivered", icon: HomeIcon, done: false },
];

export function OrderSuccessPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!state) navigate("/", { replace: true });
  }, [state, navigate]);

  if (!state) return null;

  const { orderId, total, items, name } = state;

  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 5);
  const deliveryLabel = deliveryDate.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const orderDateLabel = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <section className="pt-32 pb-24 px-6 md:px-10 min-h-screen" style={{ background: "#0B0D10" }}>
      <div className="max-w-2xl mx-auto text-center">
        <CheckoutSteps current="done" />

        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8 checkmark-pop"
          style={{ background: "rgba(30,154,255,0.12)" }}
        >
          <CheckCircle2 size={38} color="#1E9AFF" />
        </div>

        <p className="uppercase tracking-widest text-xs mb-3" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#FF8A00" }}>
          Order Confirmed
        </p>
        <h1 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-3xl md:text-4xl mb-4">
          Thank you{name ? `, ${name.split(" ")[0]}` : ""}!
        </h1>
        <p className="text-sm mb-10" style={{ color: "rgba(245,241,234,0.6)" }}>
          Your order has been placed successfully. A confirmation has been sent to your email.
        </p>

        <div
          className="rounded-2xl p-6 text-left mb-6"
          style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="flex items-center justify-between mb-5 pb-5" style={{ borderBottom: "1px dashed rgba(255,255,255,0.15)" }}>
            <div>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(245,241,234,0.5)" }}>Order ID</p>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }} className="text-base">{orderId}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(245,241,234,0.5)" }}>Order Date</p>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }} className="text-base">{orderDateLabel}</p>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-5 pb-5" style={{ borderBottom: "1px dashed rgba(255,255,255,0.15)" }}>
            {items.map((item) => (
              <div key={item.name} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full" style={{ objectFit: "cover" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-sm truncate">{item.name}</p>
                  <p className="text-xs" style={{ color: "rgba(245,241,234,0.5)" }}>Qty {item.qty}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <span className="text-sm" style={{ color: "rgba(245,241,234,0.6)" }}>Amount Paid</span>
            <span style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl">₹{formatINR(total)}</span>
          </div>

          <div>
            <div className="flex items-center justify-between relative mb-2">
              <div
                className="absolute left-0 right-0 top-4 h-0.5"
                style={{ background: "rgba(255,255,255,0.1)", zIndex: 0 }}
              ></div>
              <div
                className="absolute left-0 top-4 h-0.5"
                style={{ background: "#1E9AFF", width: "8%", zIndex: 0 }}
              ></div>
              {TRACKING_STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="relative z-10 flex flex-col items-center gap-2" style={{ flex: 1 }}>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        background: step.done ? "#1E9AFF" : "#1E2128",
                        border: step.done ? "none" : "1px solid rgba(255,255,255,0.15)",
                      }}
                    >
                      <Icon size={14} color={step.done ? "#fff" : "rgba(245,241,234,0.35)"} />
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-widest text-center"
                      style={{ color: step.done ? "#F5F1EA" : "rgba(245,241,234,0.35)" }}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-center mt-4" style={{ color: "rgba(245,241,234,0.5)" }}>
              Estimated delivery by <span style={{ color: "#F5F1EA" }}>{deliveryLabel}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs mb-10" style={{ color: "rgba(245,241,234,0.4)" }}>
          <Package size={13} /> You'll get tracking details by email once your order ships.
        </div>

        <Link
          to="/"
          className="btn-primary px-8 py-3.5 rounded-full text-sm tracking-widest uppercase inline-flex items-center gap-2"
        >
          Continue Shopping <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export default OrderSuccessPage;
